// features/chat/ChatList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserChats } from '../services/chatService';
import { 
  MessageCircle, 
  Clock, 
  User, 
  Package,
  ArrowRight,
  Calendar,
  IndianRupee
} from 'lucide-react';

const ChatList = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadChats = async () => {
      if (!user) return;
      
      try {
        const userChats = await getUserChats(user.uid, user.role);
        setChats(userChats);
      } catch (error) {
        console.error('Error loading chats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChats();
  }, [user]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Your Conversations</h1>
        <div className="flex items-center space-x-2">
          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
            <MessageCircle className="h-5 w-5 text-orange-600" />
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{chats.length} Conversations</p>
            <p className="text-xs text-gray-500">Active chats</p>
          </div>
        </div>
      </div>
      
      {chats.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No conversations yet</p>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
            Start a chat from a product page to connect with {user?.role === 'buyer' ? 'sellers' : 'buyers'}
          </p>
          {user?.role === 'buyer' && (
            <Link
              to="/buyer/products"
              className="inline-flex items-center mt-4 text-orange-600 hover:text-orange-700"
            >
              Browse Products <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {chats.map((chat) => (
            <Link
              key={chat.id}
              to={`/chat/${chat.id}`}
              className="block bg-white p-4 rounded-lg border hover:border-orange-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{chat.productName}</h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>
                        {user?.role === 'buyer' ? 'Seller: ' : 'Buyer: '}
                        {user?.role === 'buyer' ? chat.sellerName : chat.buyerName}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                        {user?.role === 'buyer' ? 'Seller' : 'Buyer'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1 truncate max-w-md">
                      {chat.lastMessage}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>{formatTime(chat.lastMessageTime)}</span>
                  </div>
                  {chat.unreadCount && chat.unreadCount[user?.role] > 0 && (
                    <span className="inline-block mt-2 px-2 py-1 text-xs font-bold bg-orange-600 text-white rounded-full">
                      {chat.unreadCount[user?.role]} new
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatList;